/**
 * useShopPageContext — le socle commun aux TROIS pages publiques d'une
 * boutique : la vitrine (`/boutique/[slug]`), la fiche produit
 * (`/boutique/[slug]/[productSlug]`) et la page panier
 * (`/boutique/[slug]/panier`).
 *
 * Ces trois pages avaient (ou allaient avoir) la même fenêtre de vente, le
 * même message de clôture et la même liste de query à propager. Le chantier
 * « panier » en ajoutant une page, la troisième copie était de trop : elles
 * sont ici, en un seul exemplaire.
 *
 * ⚠️ Ce composable ne fait AUCUN fetch : la boutique lui est passée (elle vient
 * de `useShopPublicData`, dont la clé `useFetch` est partagée entre les pages —
 * naviguer de la vitrine au panier ne redéclenche aucun appel réseau).
 */
import type { ComputedRef, Ref } from "vue";
import type { Shop } from "~/types/shop";
import { parseDbInstant } from "@donoor/utils/dates";
import { formatDateAssociation, fuseauDe } from "~/utils/heureAssociation";

export function useShopPageContext(shop: Ref<Shop | null>) {
  const { t } = useI18n();
  const route = useRoute();

  const isPreview = computed(() => route.query.preview === "true");

  /**
   * Query à propager sur TOUS les liens internes de la boutique.
   * En mode widget (`?embed=1`) la navigation doit rester DANS l'iframe :
   * perdre ces paramètres casserait l'habillage et, à terme, le tunnel.
   */
  const carryQuery = computed<Record<string, string>>(() => {
    const q: Record<string, string> = {};
    if (route.query.embed) q.embed = String(route.query.embed);
    if (route.query.host) q.host = String(route.query.host);
    if (route.query.primary) q.primary = String(route.query.primary);
    if (isPreview.value) q.preview = "true";
    return q;
  });

  // ─── Fenêtre de vente / statut ───────────────────────────────────
  const now = ref(Date.now());
  const isClosed = computed(() => shop.value?.status === "closed");
  const saleNotStarted = computed(() => {
    const start = shop.value?.sale_start_date;
    return !!start && parseDbInstant(start).getTime() > now.value;
  });
  const saleEnded = computed(() => {
    const end = shop.value?.sale_end_date;
    // Chaîne naïve de la base = UTC (lot 2).
    return !!end && parseDbInstant(end).getTime() < now.value;
  });
  const canBuy = computed(
    () => !isClosed.value && !saleNotStarted.value && !saleEnded.value,
  );

  /** Jour de vente dans le fuseau de l'association (`timezone`, Paris par défaut). */
  function formatSaleDate(value: string): string {
    return formatDateAssociation(value, fuseauDe(shop.value), {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }

  const saleClosedMessage = computed<string | null>(() => {
    if (isClosed.value) {
      return t("boutique.pageContext.boutiqueCloturee");
    }
    if (saleNotStarted.value && shop.value?.sale_start_date) {
      return t("boutique.pageContext.venteNonDemarree", { date: formatSaleDate(shop.value.sale_start_date) });
    }
    if (saleEnded.value && shop.value?.sale_end_date) {
      return t("boutique.pageContext.venteTerminee", { date: formatSaleDate(shop.value.sale_end_date) });
    }
    return null;
  });

  const showRemainingStock = computed(
    () => shop.value?.settings?.show_remaining_stock !== false,
  );

  return {
    isPreview,
    carryQuery,
    canBuy,
    saleClosedMessage,
    showRemainingStock,
  } satisfies {
    isPreview: ComputedRef<boolean>;
    carryQuery: ComputedRef<Record<string, string>>;
    canBuy: ComputedRef<boolean>;
    saleClosedMessage: ComputedRef<string | null>;
    showRemainingStock: ComputedRef<boolean>;
  };
}
